import axios from 'axios';

const api = axios.create({
  baseURL: '',
  timeout: 30000,
  withCredentials: true,
});

const refreshClient = axios.create({
  baseURL: '',
  timeout: 10000,
  withCredentials: true,
});

const AUTH_PATHS = ['/api/auth/login', '/api/auth/register', '/api/auth/refresh', '/api/auth/logout'];

/** In-tab mutex when Web Locks API is unavailable (Fast Refresh / concurrent callers). */
const tabRefreshMutex =
  typeof window !== 'undefined'
    ? (window.__citiusTabRefreshMutex =
        window.__citiusTabRefreshMutex || { refreshInFlight: null })
    : { refreshInFlight: null };

const postRefresh = () => refreshClient.post('/api/auth/refresh');

/**
 * Single-flight refresh. Web Locks API serializes across tabs (same origin); in-memory mutex
 * covers Strict Mode / concurrent callers when locks are unavailable.
 */
export const refreshToken = () => {
  if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
    return navigator.locks.request('citius-auth-refresh', () => postRefresh());
  }
  if (tabRefreshMutex.refreshInFlight) {
    return tabRefreshMutex.refreshInFlight;
  }
  tabRefreshMutex.refreshInFlight = postRefresh().finally(() => {
    tabRefreshMutex.refreshInFlight = null;
  });
  return tabRefreshMutex.refreshInFlight;
};

let isRefreshing = false;
let failedQueue = [];

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const url = originalRequest?.url || '';

    if (AUTH_PATHS.some(p => url.includes(p))) {
      return Promise.reject(error);
    }

    // Only retry on 401 (unauthorized) — 403 is permission denied, refresh won't help
    if (status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await refreshToken();
        failedQueue.forEach(({ resolve }) => resolve());
        failedQueue = [];
        return api(originalRequest);
      } catch (refreshError) {
        failedQueue.forEach(({ reject }) => reject(refreshError));
        failedQueue = [];
        // Only log out when refresh explicitly returns 401 (invalid/expired refresh token).
        // Network errors, timeouts, server restarts should NOT trigger logout.
        if (refreshError.response?.status === 401) {
          window.dispatchEvent(new Event('authExpired'));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export default api;
